import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/login',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'Redirect',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/admin-login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    children: [],
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/MemberLogin.vue'),
    name: 'MemberLogin',
    meta: { hidden: true }
  },
  {
    path: '/aws-login',
    component: () => import('@/views/Login/AwsLogin.vue'),
    name: 'AwsLogin',
    meta: { hidden: true }
  },
  {
    path: '/password',
    name: 'password',
    meta: {
      title: 'Forgot Password',
      noTagsView: true,
      noCache: true,
      hidden: true
    },
    children: [
      {
        path: 'forgot',
        component: () => import('@/views/Password/MemberForgot.vue'),
        name: 'MemberFOrtgot',
        meta: {
          hidden: true
        }
      },
      {
        path: 'admin-forgot',
        component: () => import('@/views/Password/Forgot.vue'),
        name: 'Forgot',
        meta: {
          hidden: true,
          title: 'Forgot Password'
        }
      },
      {
        path: 'reset',
        component: () => import('@/views/Password/Reset.vue'),
        name: 'Reset',
        meta: {
          title: 'Reset Password'
        }
      }
    ]
  },
  {
    path: '/change',
    component: Layout,
    name: 'ChangePassword',
    meta: {
      title: 'Change Password',
      hidden: true
    },
    children: [
      {
        path: 'password',
        component: () => import('@/views/Password/Change.vue'),
        name: 'ChangePwd',
        meta: {
          title: 'Change Password',
          hidden: true
        }
      }
    ]
  },
  {
    path: '/register',
    component: () => import('@/views/Application/BasicApplication/BasicApplication.vue'),
    name: 'Register',
    meta: { title: t('Application.basicTraining.title'), hidden: true }
  },
  {
    path: '/card-payment',
    component: () => import('@/views/Payment/OnlinePaypal.vue'),
    name: 'OnlinePayment',
    meta: { hidden: true }
  },
  {
    path: '/success',
    component: () => import('@/views/Payment/SuccessApplication.vue'),
    name: 'SuccessApplication',
    meta: { title: t('Application.basicTraining.title'), hidden: true }
  },
  {
    path: '/paypal-return',
    component: () => import('@/views/Payment/PaypalReturn.vue'),
    name: 'PaypalReturn',
    meta: { hidden: true }
  },
  {
    path: '/paypal-pending',
    component: () => import('@/views/Payment/PaypalPending.vue'),
    name: 'PaypalPending',
    meta: { hidden: true }
  },
  {
    path: '/paypal-cancel',
    component: () => import('@/views/Payment/PaypalCancel.vue'),
    name: 'PaypalCancel',
    meta: { hidden: true }
  },
  {
    path: '/persons-recommended',
    component: () => import('@/views/SupervisorProfile/FirstPersonRecommended.vue'),
    name: 'FirstPersonRecommended',
    meta: { title: t('SupervisorProfile.personsRecommend.title'), hidden: true }
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/index',
    name: 'Dashboard',
    meta: { title: t('Dashboard.title'), icon: 'vi-tdesign:time', alwaysShow: true },
    children: [
      {
        path: 'index',
        component: () => import('@/views/Dashboard/Dashboard.vue'),
        name: 'DashboardIndex',
        meta: {
          title: t('Dashboard.title'),
          hidden: true
        }
      }
    ]
  },
  {
    path: '/supervisor-profile',
    component: Layout,
    redirect: '/supervisor-profile/personal-particular',
    name: 'SupervisorProfile',
    meta: { title: t('SupervisorProfile.title'), icon: 'vi-tdesign:user-circle', alwaysShow: true },
    children: [
      {
        path: 'personal-particular',
        component: () => import('@/views/SupervisorProfile/PersonalParticular.vue'),
        name: 'SupervisorPersonalParticular',
        meta: { title: t('SupervisorProfile.profile.title') }
      },
      {
        path: 'persons-recommended',
        component: () => import('@/views/SupervisorProfile/PersonsRecommended.vue'),
        name: 'SupervisorPersonsRecommended',
        meta: { title: t('SupervisorProfile.personsRecommend.title') }
      }
    ]
  },
  {
    path: '/view-verify-trainee',
    component: Layout,
    redirect: '/view-verify-trainee/list',
    name: 'ViewVerifyTrainee',
    meta: {
      title: t('router.viewVerifyTrainee'),
      icon: 'vi-ep:search',
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/ViewVerifyTrainee/ViewVerifyTrainee.vue'),
        name: 'ViewVerifyTraineeList',
        meta: {
          hidden: true,
          noTagsView: true,
          title: t('router.viewVerifyTrainee')
        }
      },
      {
        path: 'add',
        component: () => import('@/views/ViewVerifyTrainee/ViewVerifyTraineeAdd.vue'),
        name: 'ViewVerifyTraineeAdd',
        meta: {
          title: t('router.viewVerifyTrainee'),
          hidden: true
        }
      },
      {
        path: 'detail',
        component: () => import('@/views/ViewVerifyTrainee/ViewVerifyTraineeDetail.vue'),
        name: 'ViewVerifyTraineeDetail',
        meta: {
          title: t('router.viewVerifyTrainee'),
          hidden: true
        }
      },
      {
        path: 'rotation-edit',
        component: () => import('@/views/BasicTrainingRecords/Rotation/Detail.vue'),
        name: 'ViewRotationEdit',
        meta: { title: t('TrainingRecords.rotation.title'), hidden: true }
      },
      {
        path: 'clinical-attachment-edit',
        component: () => import('@/views/BasicTrainingRecords/ClinicalAttachment/Detail.vue'),
        name: 'ViewClinicalAttachmentEdit',
        meta: { title: t('TrainingRecords.clinicalAttachment.title'), hidden: true }
      },
      {
        path: 'consultation-session-edit',
        component: () =>
          import('@/views/BasicTrainingRecords/ConsultationSession/ConsultationSessionEdit.vue'),
        name: 'ViewConsultationSessionEdit',
        meta: { title: t('TrainingRecords.consultationSession.title'), hidden: true }
      },
      {
        path: 'community-involvement-edit',
        component: () =>
          import('@/views/BasicTrainingRecords/CommunityInvolvement/CommunityInvolvementEdit.vue'),
        name: 'ViewCommunityInvolvementEdit',
        meta: { title: t('BasicTrainingRecords.CommunityInvolvement.title'), hidden: true }
      },
      {
        path: 'structured-educational-edit',
        component: () => import('@/views/BasicTrainingRecords/StructuredEducational/Detail.vue'),
        name: 'ViewStructuredEducationalDetail',
        meta: { title: t('TrainingRecords.structuredEducational.title'), hidden: true }
      },
      {
        path: 'auditResearch-project-edit',
        name: 'ViewAuditResearchProjectEdit',
        component: () =>
          import('@/views/BasicTrainingRecords/AuditResearchProject/AuditResearchProjectEdit.vue'),
        meta: {
          title: t('BasicTrainingRecords.AuditResearchProject.title'),
          hidden: true
        }
      },
      {
        path: 'teaching-experience-edit',
        component: () =>
          import('@/views/BasicTrainingRecords/TeachingExperience/TeachingExperienceEdit.vue'),
        name: 'ViewTeachingExperienceEdit',
        meta: {
          title: t('BasicTrainingRecords.TeachingExperience.title'),
          hidden: true
        }
      },
      {
        path: 'responsibilities-edit',
        component: () =>
          import(
            '@/views/BasicTrainingRecords/CollegeActivitiesResponsibilities/CollegeActivitiesResponsibilitiesEdit.vue'
          ),
        name: 'ViewCollegeActivitiesResponsibilitiesEdit',
        meta: {
          title: t('BasicTrainingRecords.CollegeActivitiesResponsibilities.title'),
          hidden: true
        }
      },
      {
        path: 'community-based-patient-edit',
        component: () =>
          import(
            '@/views/BasicTrainingRecords/CommunityBasedTrainingPatient/CommunityBasedTrainingPatientEdit.vue'
          ),
        name: 'ViewCommunityBasedTrainingPatientEdit',
        meta: {
          title: t('BasicTrainingRecords.CommunityBasedTrainingPatient.title'),
          hidden: true
        }
      },
      {
        path: 'community-based-patient-import',
        component: () =>
          import('@/views/BasicTrainingRecords/CommunityBasedTrainingPatient/CommunityImport.vue'),
        name: 'ViewCommunityBasedTrainingPatientImport',
        meta: {
          title: t('BasicTrainingRecords.CommunityBasedTrainingPatient.title'),
          hidden: true
        }
      },
      {
        path: 'hospital-based-logdairy-import',
        component: () => import('@/views/BasicTrainingRecords/Rotation/ImportLogdairy.vue'),
        name: 'ViewLogdairyImport',
        meta: { title: '', hidden: true }
      },
      {
        path: 'annual-checking-edit',
        component: () => import('@/views/BasicTrainingRecords/AnnualChecking/Detail.vue'),
        name: 'ViewAnnualCheckingEdit',
        meta: { title: t('TrainingRecords.annualChecking.title'), hidden: true }
      }
    ]
  },
  {
    path: '/mass-view-verify',
    component: Layout,
    redirect: '/mass-view-verify/basic-training-application',
    name: 'MassView',
    meta: {
      title: t('Massview.Title'),
      icon: 'vi-ep:finished',
      alwaysShow: true
    },
    children: [
      {
        path: 'basic-training-application',
        component: () =>
          import('@/views/MassView/BasicTrainingApplication/BasicTrainingApplication.vue'),
        name: 'BasicTrainingApplication',
        meta: { title: t('Massview.basicTrainingApplication.title') },
        children: [
          {
            path: 'detail',
            component: () =>
              import(
                '@/views/MassView/BasicTrainingApplication/BasicTrainingApplicationDetail.vue'
              ),
            name: 'BasicTrainingApplicationDetail',
            meta: { hidden: true }
          }
        ]
      },
      {
        path: 'completion-application',
        component: () => import('@/views/MassView/BasicTrainingComletionApplication/Index.vue'),
        name: 'BasicCompletionApplication',
        meta: { title: t('Application.completionApplication.title') },
        children: [
          {
            path: 'detail',
            component: () =>
              import('@/views/MassView/BasicTrainingComletionApplication/Detail.vue'),
            name: 'BasicCompletionApplicationDetail',
            meta: { title: t('Application.completionApplication.title'), hidden: true }
          }
        ]
      },
      {
        path: 'rotation',
        component: () => import('@/views/MassView/Rotation/Index.vue'),
        name: 'MassViewRotationIndex',
        meta: { title: t('TrainingRecords.rotation.title') },
        children: [
          {
            path: 'detail',
            component: () => import('@/views/MassView/Rotation/Detail.vue'),
            name: 'MassViewRotationDetail',
            meta: { title: t('TrainingRecords.rotation.title'), hidden: true }
          }
        ]
      },
      {
        path: 'consultation-session',
        component: () => import('@/views/MassView/ConsultationSession/ConsultationSession.vue'),
        name: 'ConsultationSessionView',
        meta: { title: t('BasicTrainingRecords.ConsultationSession.title') },
        children: [
          {
            path: 'detail',
            component: () =>
              import('@/views/MassView/ConsultationSession/ConsultationSessionDetail.vue'),
            name: 'ConsultationSessionDetail',
            meta: { hidden: true }
          }
        ]
      },
      {
        path: 'super-assessment',
        component: () => import('@/views/MassView/SupervisorAssessment/Index.vue'),
        name: 'SupervisorAssessmentIndex',
        meta: { title: t('Massview.superVisorAssessment.title') },
        children: [
          {
            path: 'detail',
            component: () => import('@/views/MassView/SupervisorAssessment/Detail.vue'),
            name: 'SupervisorAssessmentDetail',
            meta: { title: t('Massview.superVisorAssessment.title'), hidden: true }
          }
        ]
      },
      {
        path: 'community-learning',
        component: () => import('@/views/MassView/CommunityLearningPortfolio/Index.vue'),
        name: 'CommunityLearningPortfolioIndex',
        meta: { title: t('TrainingRecords.rotation.communityPortfolio') },
        children: [
          {
            path: 'detail',
            component: () => import('@/views/MassView/CommunityLearningPortfolio/Detail.vue'),
            name: 'CommunityLearningPortfolioDetail',
            meta: { title: t('TrainingRecords.rotation.communityPortfolio'), hidden: true }
          }
        ]
      },
      {
        path: 'community-training',
        component: () => import('@/views/MassView/CommunityTrainingDairy/Index.vue'),
        name: 'CommunityTrainingDairyIndex',
        meta: { title: t('Massview.communityTrainingDairy.title') },
        children: [
          {
            path: 'detail',
            component: () => import('@/views/MassView/CommunityTrainingDairy/Detail.vue'),
            name: 'CommunityTrainingDairyDetail',
            meta: { title: t('Massview.communityTrainingDairy.title'), hidden: true }
          }
        ]
      },
      {
        path: 'clinical-attachment',
        component: () => import('@/views/MassView/ClinicalAttachment/ClinicalAttachment.vue'),
        name: 'ClinicalAttachmentIndex',
        meta: { title: t('TrainingRecords.clinicalAttachment.title') },
        children: [
          {
            path: 'detail',
            component: () =>
              import('@/views/MassView/ClinicalAttachment/ClinicalAttachmentDetail.vue'),
            name: 'clinicalAttachmentDetail',
            meta: { title: t('TrainingRecords.clinicalAttachment.title'), hidden: true }
          }
        ]
      },
      {
        path: 'annual-checking',
        component: () => import('@/views/MassView/AnnualChecking/Index.vue'),
        name: 'AnnualChecking',
        meta: { title: t('TrainingRecords.annualChecking.title') },
        children: [
          {
            path: 'detail',
            component: () => import('@/views/MassView/AnnualChecking/Detail.vue'),
            name: 'AnnualCheckingDetail',
            meta: { title: t('TrainingRecords.annualChecking.title'), hidden: true }
          },
          {
            path: 'view-logbook-trainee-detail',
            component: () => import('@/views/ViewVerifyTrainee/ViewVerifyTraineeDetail.vue'),
            name: 'ViewLogbookTraineeDetail',
            meta: {
              title: t('router.viewVerifyTrainee'),
              hidden: true
            }
          },
          {
            path: 'rotation-edit',
            component: () => import('@/views/BasicTrainingRecords/Rotation/Detail.vue'),
            name: 'ViewLogbookRotationEdit',
            meta: { title: t('TrainingRecords.rotation.title'), hidden: true }
          },
          {
            path: 'clinical-attachment-edit',
            component: () => import('@/views/BasicTrainingRecords/ClinicalAttachment/Detail.vue'),
            name: 'ViewLogbookClinicalAttachmentEdit',
            meta: { title: t('TrainingRecords.clinicalAttachment.title'), hidden: true }
          },
          {
            path: 'consultation-session-edit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/ConsultationSession/ConsultationSessionEdit.vue'
              ),
            name: 'ViewLogbookConsultationSessionEdit',
            meta: { title: t('TrainingRecords.consultationSession.title'), hidden: true }
          },
          {
            path: 'community-involvement-edit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CommunityInvolvement/CommunityInvolvementEdit.vue'
              ),
            name: 'ViewLogbookCommunityInvolvementEdit',
            meta: { title: t('BasicTrainingRecords.CommunityInvolvement.title'), hidden: true }
          },
          {
            path: 'structured-educational-edit',
            component: () =>
              import('@/views/BasicTrainingRecords/StructuredEducational/Detail.vue'),
            name: 'ViewLogbookStructuredEducationalDetail',
            meta: { title: t('TrainingRecords.structuredEducational.title'), hidden: true }
          },
          {
            path: 'auditResearch-project-edit',
            name: 'ViewLogbookAuditResearchProjectEdit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/AuditResearchProject/AuditResearchProjectEdit.vue'
              ),
            meta: {
              title: t('BasicTrainingRecords.AuditResearchProject.title'),
              hidden: true
            }
          },
          {
            path: 'teaching-experience-edit',
            component: () =>
              import('@/views/BasicTrainingRecords/TeachingExperience/TeachingExperienceEdit.vue'),
            name: 'ViewLogbookTeachingExperienceEdit',
            meta: {
              title: t('BasicTrainingRecords.TeachingExperience.title'),
              hidden: true
            }
          },
          {
            path: 'responsibilities-edit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CollegeActivitiesResponsibilities/CollegeActivitiesResponsibilitiesEdit.vue'
              ),
            name: 'ViewLogbookCollegeActivitiesResponsibilitiesEdit',
            meta: {
              title: t('BasicTrainingRecords.CollegeActivitiesResponsibilities.title'),
              hidden: true
            }
          },
          {
            path: 'community-based-patient-edit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CommunityBasedTrainingPatient/CommunityBasedTrainingPatientEdit.vue'
              ),
            name: 'ViewLogbookCommunityBasedTrainingPatientEdit',
            meta: {
              title: t('BasicTrainingRecords.CommunityBasedTrainingPatient.title'),
              hidden: true
            }
          },
          {
            path: 'community-based-patient-import',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CommunityBasedTrainingPatient/CommunityImport.vue'
              ),
            name: 'ViewLogbookCommunityBasedTrainingPatientImport',
            meta: {
              title: t('BasicTrainingRecords.CommunityBasedTrainingPatient.title'),
              hidden: true
            }
          },
          {
            path: 'hospital-based-logdairy-import',
            component: () => import('@/views/BasicTrainingRecords/Rotation/ImportLogdairy.vue'),
            name: 'ViewLogbookLogdairyImport',
            meta: { title: '', hidden: true }
          },
          {
            path: 'annual-checking-edit',
            component: () => import('@/views/BasicTrainingRecords/AnnualChecking/Detail.vue'),
            name: 'ViewLogbookAnnualCheckingEdit',
            meta: { title: t('TrainingRecords.annualChecking.title'), hidden: true }
          }
        ]
      }
    ]
  },
  {
    path: '/administration',
    component: Layout,
    redirect: '/administration/user-admin',
    name: 'Administration',
    meta: {
      title: t('Administration.Title'),
      icon: 'vi-ep:setting',
      alwaysShow: true
    },
    children: [
      {
        path: 'user-admin',
        component: () => import('@/views/Administration/UserAdmin/UserAdmin.vue'),
        name: 'UserAdmin',
        meta: {
          title: t('Administration.UserAdmin.Title')
        },
        children: [
          {
            path: 'user-admin-add',
            component: () => import('@/views/Administration/UserAdmin/UserAdminAdd.vue'),
            name: 'UserAdminAdd',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'user-admin-edit',
            component: () => import('@/views/Administration/UserAdmin/UserAdminEdit.vue'),
            name: 'UserAdminEdit',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          }
        ]
      },
      {
        path: 'cos-training-coodinator',
        component: () =>
          import('@/views/Administration/COSTrainingCoodinator/COSTrainingCoodinator.vue'),
        name: 'COSTrainingCoodinator',
        meta: { title: t('Administration.trainingCoodinator.title') },
        children: [
          {
            path: 'add',
            component: () => import('@/views/Administration/COSTrainingCoodinator/Add.vue'),
            name: 'COSTrainingCoodinatorAdd',
            meta: { title: t('Administration.trainingCoodinator.title'), hidden: true }
          },
          {
            path: 'edit',
            component: () => import('@/views/Administration/COSTrainingCoodinator/Edit.vue'),
            name: 'COSTrainingCoodinatorEdit',
            meta: { title: t('Administration.trainingCoodinator.title'), hidden: true }
          }
        ]
      },
      {
        path: 'supervisor',
        component: () => import('@/views/Administration/Supervisor/Supervisor.vue'),
        name: 'Supervisor',
        meta: { title: t('Administration.Supervisor.title') },
        children: [
          {
            path: 'add',
            component: () => import('@/views/Administration/Supervisor/SuperVisorAdd.vue'),
            name: 'SupervisorAdd',
            meta: { title: t('Administration.Supervisor.newJoin'), hidden: true }
          },
          {
            path: 'edit',
            component: () => import('@/views/Administration/Supervisor/SupervisorEdit.vue'),
            name: 'SupervisorEdit',
            meta: { title: t('Administration.Supervisor.newJoin'), hidden: true }
          },
          {
            path: 'declaration',
            component: () => import('@/views/Administration/Supervisor/Declaration.vue'),
            name: 'SupervisorDeclaration',
            meta: { title: t('Administration.Supervisor.newJoin'), hidden: true }
          }
        ]
      },
      {
        path: 'training-center',
        component: () => import('@/views/Administration/TrainingCenter/TrainingCenter.vue'),
        name: 'TrainingCenter',
        meta: { title: t('Administration.TrainingCenter.title') },
        children: [
          {
            path: 'add',
            component: () => import('@/views/Administration/TrainingCenter/Add.vue'),
            name: 'TrainingCenterAdd',
            meta: { title: t('Administration.TrainingCenter.title'), hidden: true }
          },
          {
            path: 'edit',
            component: () => import('@/views/Administration/TrainingCenter/Edit.vue'),
            name: 'TrainingCenterEdit',
            meta: { title: t('Administration.TrainingCenter.title'), hidden: true }
          }
        ]
      },
      {
        path: 'master-table',
        component: () => import('@/views/Administration/MasterTable/MasterTable.vue'),
        name: 'MasterTable',
        meta: {
          title: t('Administration.MasterTable.Title')
        },
        children: [
          {
            path: 'specialty',
            component: () =>
              import('@/views/Administration/MasterTable/Administration/Specialty/Specialty.vue'),
            name: 'Specialty',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'training-center-nature',
            component: () =>
              import(
                '@/views/Administration/MasterTable/Administration/TrainingCenterNature/TrainingCenterNature.vue'
              ),
            name: 'TrainingCenterNature',
            meta: {
              title: t('Administration.MasterTable.trainingCenterNature.title'),
              hidden: true
            }
          },
          {
            path: 'EducationalProgrammeModule',
            component: () =>
              import(
                '@/views/Administration/MasterTable/Administration/EducationalProgrammeModule/EducationalProgrammeModule.vue'
              ),
            name: 'EducationalProgrammeModule',
            meta: {
              title: t('Administration.MasterTable.EducationalProgrammeModule.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'hospital',
            component: () =>
              import('@/views/Administration/MasterTable/Administration/Hospital/Hospital.vue'),
            name: 'Hospital',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'cluster',
            component: () =>
              import(
                '@/views/Administration/MasterTable/Administration/HospitalCluster/HospitalCluster.vue'
              ),
            name: 'Hospital/cluster',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'college',
            component: () =>
              import('@/views/Administration/MasterTable/Administration/College/College.vue'),
            name: 'College',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'college-add',
            component: () =>
              import('@/views/Administration/MasterTable/Administration/College/CollegeAdd.vue'),
            name: 'CollegeAdd',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'college-edit',
            component: () =>
              import('@/views/Administration/MasterTable/Administration/College/CollegeEdit.vue'),
            name: 'CollegeEdit',
            meta: {
              title: t('Administration.MasterTable.College.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },

          {
            path: 'hospital-add',
            component: () =>
              import('@/views/Administration/MasterTable/Administration/Hospital/HospitalAdd.vue'),
            name: 'HospitalAdd',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'hospital-edit',
            component: () =>
              import('@/views/Administration/MasterTable/Administration/Hospital/HospitalEdit.vue'),
            name: 'HospitalEdit',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'hospital-edit',
            component: () =>
              import('@/views/Administration/MasterTable/Administration/Hospital/HospitalEdit.vue'),
            name: 'HospitalEdit',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'hospital-cluster-add',
            component: () =>
              import(
                '@/views/Administration/MasterTable/Administration/HospitalCluster/HospitalClusterAdd.vue'
              ),
            name: 'HospitalClusterAdd',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'hospital-cluster-edit',
            component: () =>
              import(
                '@/views/Administration/MasterTable/Administration/HospitalCluster/HospitalClusterEdit.vue'
              ),
            name: 'HospitalClusterEdit',
            meta: {
              title: t('Administration.MasterTable.Specialty.Title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'examination-for-exemption',
            component: () =>
              import(
                '@/views/Administration/MasterTable/Administration/Examination/Examination.vue'
              ),
            name: 'Examination',
            meta: {
              title: t('Administration.MasterTable.Examination.title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'examination-for-exemption-add',
            component: () =>
              import(
                '@/views/Administration/MasterTable/Administration/Examination/ExaminationAdd.vue'
              ),
            name: 'ExaminationAdd',
            meta: {
              title: t('Administration.MasterTable.Examination.title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'examination-for-exemption-edit',
            component: () =>
              import(
                '@/views/Administration/MasterTable/Administration/Examination/ExaminationEdit.vue'
              ),
            name: 'ExaminationEdit',
            meta: {
              title: t('Administration.MasterTable.Examination.title'),
              noTagsView: true,
              noCache: true,
              hidden: true
            }
          },
          {
            path: 'certificate-signature',
            component: () =>
              import(
                '@/views/Administration/MasterTable/Administration/CertificateSignature/Index.vue'
              ),
            name: 'CertificateSignature',
            meta: {
              title: t('Administration.MasterTable.CertificateSignature.title'),
              hidden: true
            }
          }
        ]
      },
      {
        path: 'system-parameter',
        component: () => import('@/views/Administration/SystemParameter/SystemParameter.vue'),
        name: 'SystemParameter',
        meta: {
          title: t('Administration.systemParameter.title')
        },
        children: []
      },
      {
        path: 'report',
        component: () => import('@/views/Administration/Report/Report.vue'),
        name: 'Report',
        meta: {
          title: t('Administration.Report.title')
        },
        children: [
          {
            path: 'detail',
            component: () => import('@/views/Administration/Report/ReportDetail.vue'),
            name: 'ReportDetail',
            meta: {
              title: t('Administration.Report.title'),
              hidden: true
            }
          },
          {
            path: 'transaction',
            component: () => import('@/views/Administration/Report/TransactionLog.vue'),
            name: 'Transaction',
            meta: { hidden: true }
          }
        ]
      }
    ]
  },
  {
    path: '/trainee-profile',
    component: Layout,
    redirect: '/trainee-profile/personal-particular',
    name: 'TraineeProfile',
    meta: {
      title: t('profile.Title'),
      icon: 'vi-tdesign:user-circle',
      alwaysShow: true
    },
    children: [
      {
        path: 'personal-particular',
        component: () => import('@/views/TraineeProfile/PersonalParticular.vue'),
        name: 'PersonalParticular',
        meta: {
          title: t('profile.personalParticular.Title')
        }
      }
    ]
  },
  {
    path: '/training-logbook',
    component: Layout,
    redirect: '/training-logbook/rotation',
    name: 'TrainingLogbook',
    meta: {
      title: t('TrainingRecords.title'),
      icon: 'svg-icon:traing-record',
      alwaysShow: true
    },
    children: [
      {
        path: 'rotation',
        component: () => import('@/views/BasicTrainingRecords/Rotation/Index.vue'),
        name: 'Rotation',
        meta: { title: t('TrainingRecords.rotation.title') },
        children: [
          {
            path: 'add',
            component: () => import('@/views/BasicTrainingRecords/Rotation/Add.vue'),
            name: 'RotationAdd',
            meta: { title: t('TrainingRecords.rotation.title'), hidden: true }
          },
          {
            path: 'edit',
            component: () => import('@/views/BasicTrainingRecords/Rotation/Detail.vue'),
            name: 'RotationEdit',
            meta: { title: t('TrainingRecords.rotation.title'), hidden: true }
          },
          {
            path: 'logdairy-import',
            component: () => import('@/views/BasicTrainingRecords/Rotation/ImportLogdairy.vue'),
            name: 'LogdairyImport',
            meta: { title: '', hidden: true }
          }
        ]
      },
      {
        path: 'clinical-attachment',
        component: () => import('@/views/BasicTrainingRecords/ClinicalAttachment/Index.vue'),
        name: 'ClinicalAttachment',
        meta: { title: t('TrainingRecords.clinicalAttachment.title') },
        children: [
          {
            path: 'add',
            component: () => import('@/views/BasicTrainingRecords/ClinicalAttachment/Add.vue'),
            name: 'ClinicalAttachmentAdd',
            meta: { title: t('TrainingRecords.clinicalAttachment.title'), hidden: true }
          },
          {
            path: 'edit',
            component: () => import('@/views/BasicTrainingRecords/ClinicalAttachment/Detail.vue'),
            name: 'ClinicalAttachmentEdit',
            meta: { title: t('TrainingRecords.clinicalAttachment.title'), hidden: true }
          }
        ]
      },
      {
        path: 'qualification',
        component: () => import('@/views/BasicTrainingRecords/Qualification/Qualification.vue'),
        name: 'Qualification',
        meta: { title: t('TrainingRecords.qualification.title') }
      },
      {
        path: 'structured-educational',
        component: () => import('@/views/BasicTrainingRecords/StructuredEducational/Index.vue'),
        name: 'StructuredEducational',
        meta: { title: t('TrainingRecords.structuredEducational.title') },
        children: [
          {
            path: 'detail',
            component: () =>
              import('@/views/BasicTrainingRecords/StructuredEducational/Detail.vue'),
            name: 'StructuredEducationalDetail',
            meta: { title: t('TrainingRecords.structuredEducational.title'), hidden: true }
          }
        ]
      },
      {
        path: 'consultation-session',
        component: () =>
          import('@/views/BasicTrainingRecords/ConsultationSession/ConsultationSession.vue'),
        name: 'ConsultationSession',
        meta: { title: t('TrainingRecords.consultationSession.title') },
        children: [
          {
            path: 'add',
            component: () =>
              import('@/views/BasicTrainingRecords/ConsultationSession/ConsultationSessionAdd.vue'),
            name: 'ConsultationSessionAdd',
            meta: { title: t('TrainingRecords.clinicalAttachment.title'), hidden: true }
          },
          {
            path: 'edit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/ConsultationSession/ConsultationSessionEdit.vue'
              ),
            name: 'ConsultationSessionEdit',
            meta: { title: t('TrainingRecords.clinicalAttachment.title'), hidden: true }
          }
        ]
      },
      {
        path: 'community-involvement',
        name: 'CommunityInvolvement',
        component: () =>
          import('@/views/BasicTrainingRecords/CommunityInvolvement/CommunityInvolvement.vue'),
        meta: {
          title: t('BasicTrainingRecords.CommunityInvolvement.title')
        },
        children: [
          {
            path: 'add',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CommunityInvolvement/CommunityInvolvementAdd.vue'
              ),
            name: 'CommunityInvolvementAdd',
            meta: {
              title: t('BasicTrainingRecords.CommunityInvolvement.title'),
              hidden: true
            }
          },
          {
            path: 'edit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CommunityInvolvement/CommunityInvolvementEdit.vue'
              ),
            name: 'CommunityInvolvementEdit',
            meta: {
              title: t('BasicTrainingRecords.CommunityInvolvement.title'),
              hidden: true
            }
          }
        ]
      },
      {
        path: 'auditResearch-project',
        name: 'AuditResearchProject',
        component: () =>
          import('@/views/BasicTrainingRecords/AuditResearchProject/AuditResearchProject.vue'),
        meta: {
          title: t('BasicTrainingRecords.AuditResearchProject.title')
        },
        children: [
          {
            path: 'add',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/AuditResearchProject/AuditResearchProjectAdd.vue'
              ),
            name: 'AuditResearchProjectAdd',
            meta: {
              title: t('BasicTrainingRecords.AuditResearchProject.title'),
              hidden: true
            }
          },
          {
            path: 'edit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/AuditResearchProject/AuditResearchProjectEdit.vue'
              ),
            name: 'AuditResearchProjectEdit',
            meta: {
              title: t('BasicTrainingRecords.AuditResearchProject.title'),
              hidden: true
            }
          }
        ]
      },
      {
        path: 'teaching-experience',
        name: 'TeachingExperience',
        component: () =>
          import('@/views/BasicTrainingRecords/TeachingExperience/TeachingExperience.vue'),
        meta: {
          title: t('BasicTrainingRecords.TeachingExperience.title')
        },
        children: [
          {
            path: 'add',
            component: () =>
              import('@/views/BasicTrainingRecords/TeachingExperience/TeachingExperienceAdd.vue'),
            name: 'TeachingExperienceAdd',
            meta: {
              title: t('BasicTrainingRecords.TeachingExperience.title'),
              hidden: true
            }
          },
          {
            path: 'edit',
            component: () =>
              import('@/views/BasicTrainingRecords/TeachingExperience/TeachingExperienceEdit.vue'),
            name: 'TeachingExperienceEdit',
            meta: {
              title: t('BasicTrainingRecords.TeachingExperience.title'),
              hidden: true
            }
          }
        ]
      },
      {
        path: 'responsibilities',
        name: 'CollegeActivitiesResponsibilities',
        component: () =>
          import(
            '@/views/BasicTrainingRecords/CollegeActivitiesResponsibilities/CollegeActivitiesResponsibilities.vue'
          ),
        meta: {
          title: t('BasicTrainingRecords.CollegeActivitiesResponsibilities.title')
        },
        children: [
          {
            path: 'add',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CollegeActivitiesResponsibilities/CollegeActivitiesResponsibilitiesAdd.vue'
              ),
            name: 'CollegeActivitiesResponsibilitiesAdd',
            meta: {
              title: t('BasicTrainingRecords.CollegeActivitiesResponsibilities.title'),
              hidden: true
            }
          },
          {
            path: 'edit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CollegeActivitiesResponsibilities/CollegeActivitiesResponsibilitiesEdit.vue'
              ),
            name: 'CollegeActivitiesResponsibilitiesEdit',
            meta: {
              title: t('BasicTrainingRecords.CollegeActivitiesResponsibilities.title'),
              hidden: true
            }
          }
        ]
      },
      {
        path: 'community-based-patient',
        name: 'CommunityBasedTrainingPatient',
        component: () =>
          import(
            '@/views/BasicTrainingRecords/CommunityBasedTrainingPatient/CommunityBasedTrainingPatient.vue'
          ),
        meta: {
          title: t('BasicTrainingRecords.CommunityBasedTrainingPatient.title')
        },
        children: [
          {
            path: 'add',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CommunityBasedTrainingPatient/CommunityBasedTrainingPatientAdd.vue'
              ),
            name: 'CommunityBasedTrainingPatientAdd',
            meta: {
              title: t('BasicTrainingRecords.CommunityBasedTrainingPatient.title'),
              hidden: true
            }
          },
          {
            path: 'edit',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CommunityBasedTrainingPatient/CommunityBasedTrainingPatientEdit.vue'
              ),
            name: 'CommunityBasedTrainingPatientEdit',
            meta: {
              title: t('BasicTrainingRecords.CommunityBasedTrainingPatient.title'),
              hidden: true
            }
          },
          {
            path: 'import',
            component: () =>
              import(
                '@/views/BasicTrainingRecords/CommunityBasedTrainingPatient/CommunityImport.vue'
              ),
            name: 'CommunityBasedTrainingPatientImport',
            meta: {
              title: t('BasicTrainingRecords.CommunityBasedTrainingPatient.title'),
              hidden: true
            }
          }
        ]
      },
      {
        path: 'annual-checking',
        component: () => import('@/views/BasicTrainingRecords/AnnualChecking/Index.vue'),
        name: 'AnnualChecking',
        meta: { title: t('TrainingRecords.annualChecking.title') },
        children: [
          {
            path: 'add',
            component: () => import('@/views/BasicTrainingRecords/AnnualChecking/Add.vue'),
            name: 'AnnualCheckingAdd',
            meta: { title: t('TrainingRecords.annualChecking.title'), hidden: true }
          },
          {
            path: 'edit',
            component: () => import('@/views/BasicTrainingRecords/AnnualChecking/Detail.vue'),
            name: 'AnnualCheckingEdit',
            meta: { title: t('TrainingRecords.annualChecking.title'), hidden: true }
          }
        ]
      }
    ]
  },
  {
    path: '/application',
    name: 'Application',
    component: Layout,
    redirect: '/application/training-application',
    meta: { title: t('BasicApplication.title'), icon: 'vi-tdesign:book', alwaysShow: true },
    children: [
      {
        path: 'training-application',
        name: 'TrainingApplication',
        component: () =>
          import('@/views/Application/BasicApplication/BasicTrainingApplication.vue'),
        meta: { title: t('BasicApplication.basicTrainingApplication.title') }
      },
      {
        path: 'conjoint-examination',
        component: () => import('@/views/BasicTrainingRecords/ConjointExamination/Index.vue'),
        name: 'ConjointExamination',
        meta: { title: t('TrainingRecords.conjointExamination.title') }
      },
      {
        path: 'completion-application',
        name: 'CompletionApplication',
        component: () => import('@/views/Application/BasicCompletionApplication/Index.vue'),
        meta: { title: t('Application.completionApplication.title') },
        children: [
          {
            path: 'paypal-return',
            name: 'CompletionApplicationReturn',
            component: () =>
              import('@/views/Application/BasicCompletionApplication/CompletionPaypalReturn.vue'),
            meta: { hidden: true }
          },
          {
            path: 'paypal-pending',
            name: 'CompletionApplicationPending',
            component: () =>
              import('@/views/Application/BasicCompletionApplication/CompletionPaypalPending.vue'),
            meta: { hidden: true }
          },
          {
            path: 'paypal-cancel',
            name: 'CompletionApplicationCancel',
            component: () =>
              import('@/views/Application/BasicCompletionApplication/CompletionPaypalCancel.vue'),
            meta: { hidden: true }
          },
          {
            path: '/completion-success',
            component: () =>
              import(
                '@/views/Application/BasicCompletionApplication/CompletionApplicationSuccess.vue'
              ),
            name: 'CompletionApplicationSuccess',
            meta: { hidden: true }
          }
        ]
      }
    ]
  },
  {
    path: '/training_center',
    name: 'TrainingCenters',
    component: Layout,
    redirect: '/training_center/training-center',
    meta: {
      title: t('Administration.TrainingCenter.title'),
      icon: 'vi-ep:setting',
      alwaysShow: true
    },
    children: [
      {
        path: 'training-center',
        name: 'TrainingCenterIndex',
        component: () => import('@/views/Administration/TrainingCenter/TrainingCenter.vue'),
        meta: { title: t('Administration.TrainingCenter.title') },
        children: [
          {
            path: 'detail',
            name: 'TrainingCenterDetail',
            component: () => import('@/views/Administration/TrainingCenter/Edit.vue'),
            meta: { title: t('Administration.TrainingCenter.title'), hidden: true }
          }
        ]
      },
      {
        path: 'supervisor',
        name: 'SupervisorIndex',
        component: () => import('@/views/Administration/Supervisor/Supervisor.vue'),
        meta: { title: t('Administration.Supervisor.title') },
        children: [
          {
            path: 'add',
            component: () => import('@/views/Administration/Supervisor/SuperVisorAdd.vue'),
            name: 'TrainingCenterSupervisorAdd',
            meta: { title: t('Administration.Supervisor.newJoin'), hidden: true }
          },
          {
            path: 'detail',
            name: 'SupervisorDetail',
            component: () => import('@/views/Administration/Supervisor/SupervisorEdit.vue'),
            meta: { title: t('Administration.TrainingCenter.title'), hidden: true }
          },
          {
            path: 'declaration',
            component: () => import('@/views/Administration/Supervisor/Declaration.vue'),
            name: 'TrainingCenterDeclaration',
            meta: { title: t('Administration.Supervisor.newJoin'), hidden: true }
          }
        ]
      }
    ]
  },
  {
    path: '/error',
    component: Layout,
    redirect: '/error/404',
    name: 'Error',
    meta: {
      title: t('router.errorPage'),
      icon: 'vi-ci:error',
      alwaysShow: true
    },
    children: [
      {
        path: '404-demo',
        component: () => import('@/views/Error/404.vue'),
        name: '404Demo',
        meta: {
          title: '404'
        }
      },
      {
        path: '403-demo',
        component: () => import('@/views/Error/403.vue'),
        name: '403Demo',
        meta: {
          title: '403'
        }
      },
      {
        path: '500-demo',
        component: () => import('@/views/Error/500.vue'),
        name: '500Demo',
        meta: {
          title: '500'
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
